import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { Client, CommandStatus, LoadProfileBufferDataOut, OnDemandDetailsDataIn, PageInfo } from '../../../../../api/api-reference';
import { UnitConverter } from '../../../../../pipes/unit-conversion';
import { OnDemandReadStateService } from '../../../../../services/on-demand-read-state.service';

@Component({
  selector: 'app-run-read-modal',
  standalone: true,
  imports: [CommonModule, AngularSvgIconModule, TranslateModule, DatePipe, UnitConverter],
  templateUrl: './run-read-modal.component.html',
  styleUrl: './run-read-modal.component.scss',
})
export class RunReadModalComponent implements OnInit, OnDestroy {
  @Input() commandId!: string;
  @Output() closeSignal = new EventEmitter<void>();

  readonly CommandStatus = CommandStatus;
  status: CommandStatus | undefined;
  reading: LoadProfileBufferDataOut | undefined;
  errorMessage: string | undefined;
  noDataAt: Date | undefined;

  private onDemandReadSubscription?: Subscription;

  constructor(private client: Client, private toastr: ToastrService, private onDemandReadState: OnDemandReadStateService) {}

  async ngOnInit(): Promise<void> {
    // 'reads-on-demand' is owned by OnDemandReadStateService, so this only subscribes to it
    // instead of registering the event itself.
    void this.onDemandReadState.ensureSubscribed();
    this.onDemandReadSubscription = this.onDemandReadState.results$.subscribe((message) => {
      if (message.commandId !== this.commandId) return;

      this.status = message.commandStatus;

      if (message.commandStatus === CommandStatus.Success) {
        this.loadResult();
      } else if (message.commandStatus === CommandStatus.Failed || message.commandStatus === CommandStatus.ErrorOccured) {
        try {
          this.errorMessage = JSON.parse(message.errorMessage!).Message;
        } catch {
          this.errorMessage = message.errorMessage;
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.onDemandReadSubscription?.unsubscribe();
  }

  private loadResult(): void {
    const requestData = new OnDemandDetailsDataIn({
      commandId: this.commandId,
      pageInfo: new PageInfo({ page: 1, pageSize: 1 }),
    });

    this.client.getOnDemandLoadProfile(requestData).subscribe({
      next: (response) => {
        this.reading = response.data?.[0];
        if (!this.reading) this.noDataAt = new Date();
      },
      error: (err) => this.toastr.error(err),
    });
  }

  close(): void {
    this.closeSignal.emit();
  }

  get isLoading(): boolean {
    return this.status !== CommandStatus.Success && this.status !== CommandStatus.Failed && this.status !== CommandStatus.ErrorOccured;
  }

  get isFailed(): boolean {
    return this.status === CommandStatus.Failed || this.status === CommandStatus.ErrorOccured;
  }
}
